import axios, { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { HttpResponse } from 'interfaces/httpClient';
import {
	ClientNotificationsPayload,
	PasswordPayload,
	PaymentType,
	PersonalDetailsI,
	ResponseLawyers,
	UpdateUser,
	UserI,
} from 'interfaces';

/**
 * This class should hold all the routes to the endpoints that are
 * related to user handling inside the app.
 *
 * Note: Routes might change depending on the project
 * so its needed to check those before using it
 */
class UserService {
	constructor(private client: AxiosInstance) {}

	async getAll(): Promise<HttpResponse<UserI[]>> {
		return this.client.get('/users');
	}

	async getUserMeWithToken(token: string): Promise<HttpResponse<UserI>> {
		const client = axios.create({
			baseURL: process.env.NEXT_PUBLIC_API,
			// headers: {
			// 	Authorization: `Bearer ${token}`,
			// },
		});

		const response = await client.get<HttpResponse<UserI>>(
			`/auth/unsubscribe/${token}`
		);
		return response.data;
	}

	async getUserMe(): Promise<HttpResponse<UserI>> {
		return this.client.get('/user/me');
	}

	async getById(id: number): Promise<HttpResponse<UserI>> {
		return this.client.get(`/users/${id}`);
	}

	async create(data: Partial<UserI>): Promise<HttpResponse<UserI>> {
		return this.client.post('/users', data);
	}

	async update(id: number, data: Partial<UserI>): Promise<HttpResponse<UserI>> {
		return this.client.put(`/users/${id}`, data);
	}

	async completeProfile(
		data: Partial<UpdateUser>
	): Promise<HttpResponse<UserI>> {
		return this.client.put(`/user/complete-profile`, data);
	}

	async delete(): Promise<HttpResponse<any>> {
		return this.client.delete(`/user`);
	}

	/* Personal Information */

	async updatePersonalInformation(
		data: PersonalDetailsI
	): Promise<HttpResponse<{ message: string }>> {
		const formData = new FormData();
		if (data?.doc) {
			formData.append('doc', data.doc);
		}
		if (data?.phoneNumber) {
			formData.append('phoneNumber', String(data.phoneNumber));
		}
		if (data?.additionalEmail) {
			formData.append('additionalEmail', data.additionalEmail);
		}
		if (data.removeProfilePicture) {
			formData.append(
				'removeProfilePicture',
				String(data.removeProfilePicture)
			);
		}

		formData.append('firstName', data.firstName);
		formData.append('lastName', data.lastName);

		data.address1 && formData.append('address1', data.address1);
		data.address2 && formData.append('address2', data.address2);
		data.city && formData.append('city', data.city);
		data.state && formData.append('state', data.state);
		data.zipCode && formData.append('zipCode', String(data.zipCode));

		formData.append('receiveUpdates', String(data.receiveUpdates));

		return this.client.post('/user/me/personal-information', formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}

	async updatePassword(data: PasswordPayload): Promise<HttpResponse<UserI>> {
		return this.client.put('/user/change-password', data);
	}
	async updateNotificationsPreferences(
		data: ClientNotificationsPayload
	): Promise<HttpResponse<UserI>> {
		return this.client.put('/user/notification-preferences', data);
	}
	/**
	 * Update notification preferences using an explicit Bearer token instead of the logged-in session.
	 * Useful for flows like email unsubscribe links where the user might not be logged in.
	 */
	async updateNotificationsPreferencesWithId(
		userId: string,
		data: ClientNotificationsPayload
	): Promise<HttpResponse<UserI>> {
		const client = axios.create({
			baseURL: process.env.NEXT_PUBLIC_API,
		});

		const response = await client.post<HttpResponse<UserI>>(
			`/auth/update-notifications-options/${userId}`,
			data
		);
		return response.data;
	}
	async update2FA(): Promise<HttpResponse<UserI>> {
		return this.client.get('/otp/toggle_2fa');
	}
	async updatePaymentPreference(
		paymentPreference: PaymentType
	): Promise<HttpResponse<UserI>> {
		return this.client.patch('/user/payment-preference', { paymentPreference });
	}

	/* Lawyer */

	async getLawyers(): Promise<HttpResponse<ResponseLawyers>> {
		return this.client.get('/user/lawyers');
	}
}

export const userService = new UserService(axiosClient);
